/**
 * features/administration/hooks.ts — TanStack Query read hooks for the whole
 * Administration area (Story UI-U6, Task 5).
 *
 * Consolidated into ONE module (a documented deviation from the story's
 * "one hook file per page" guidance — kept together to minimise import churn;
 * each hook is still page-scoped). Every hook is the `useCarrierRows` live/mock
 * split: live → the typed api client; mock → a deterministic fixture with
 * `initialData` + `staleTime: Infinity` so screens render standalone in tests.
 *
 * Mutations live inline in the pages (useMutation + qk invalidation) — they
 * ALWAYS call the api client (tests mock the client / rely on mock mode).
 *
 * [Source: apps/web/src/features/carriers/hooks/useCarrierRows.ts]
 */

import { useInfiniteQuery, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  AdminMembersResponse,
  AuditEntry,
  ChannelConfigDto,
  ChannelMatrixEntryDto,
  PortalConfigDto,
  AuditListResponse,
  HealthStatus,
  VisibilityKeyDefinition,
  RoleVisibilityDeniesResponse,
  FieldPermissionKeyDefinition,
  RoleFieldPermissionsResponse,
  EmailBlacklistListResponse,
  ControlBacklogListResponse,
  ProcessReminderConfigResponseDto,
  MahnwesenProcessConfigDto,
  TextTemplateDto,
  RegelwerkListResponse,
  RegelwerkSollDeckungItem,
  RegelwerkAttributSchemaItem,
} from '@brokly/shared';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { apiClient, ApiError } from '../../lib/api-client';
import { useEffectivePermissions } from './useEffectivePermissions';
import { myBlocklistMockRules } from '../blocklist/useMyBlocklist';
import type { PortalAccessResponse } from '../../lib/api-client';
import type {
  RoleDto,
  CatalogResponseDto,
  UserRolesDto,
  FunktionsgruppenListResponse,
  FunktionsgruppeDetailDto,
  KundenteamListResponse,
  KundenteamDetailDto,
  SuppressionEntryDto,
  ToleranceConfigEntryDto,
  ConfidencePolicyEntryDto,
  GroupRoutingEntryDto,
  TenantConfigDto,
  AvbDocumentResponse,
  PortalGrantAdminItem,
} from './types';
import {
  MOCK_MEMBERS,
  MOCK_ROLES,
  MOCK_CATALOG,
  MOCK_USER_ROLES,
  MOCK_VISIBILITY_CATALOG,
  MOCK_FIELD_CATALOG,
  MOCK_FIELD_PERMISSIONS,
  MOCK_TEAMS,
  MOCK_TEAM_DETAIL,
  MOCK_KUNDENTEAMS,
  MOCK_KUNDENTEAM_DETAIL,
  MOCK_CONTROL_BACKLOG,
  MOCK_PROCESS_REMINDER_CONFIG,
  mockMahnwesenConfig,
  mockTextTemplates,
  MOCK_REGELWERK_SOLL,
  MOCK_REGELWERK_SCHEMA,
  MOCK_TOLERANCE,
  MOCK_CHANNELS,
  MOCK_CHANNEL_MATRIX,
  MOCK_CONFIDENCE,
  MOCK_GROUP_ROUTING,
  MOCK_TENANT,
  MOCK_PORTAL_CONFIG,
  MOCK_PORTAL_ACCESS,
  MOCK_PORTAL_GRANTS,
  MOCK_AVB,
  MOCK_AUDIT,
  MOCK_HEALTH,
} from './mock';

/** Generic live/mock read (the useCarrierRows shape). */
function useAdminRead<T>(
  queryKey: readonly unknown[],
  liveFn: () => Promise<T>,
  fixture: T,
  opts: {
    enabled?: boolean;
    /** Overrides the app-wide `retry: 1` for this read only (main.tsx). */
    retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  } = {},
): UseQueryResult<T> {
  const live = isLiveData();
  return useQuery<T>({
    queryKey,
    queryFn: () => (live ? liveFn() : Promise.resolve(fixture)),
    enabled: opts.enabled ?? true,
    ...(opts.retry === undefined ? {} : { retry: opts.retry }),
    ...(live ? {} : { initialData: fixture, staleTime: Infinity }),
  });
}

// ── Mitglieder ────────────────────────────────────────────────────────────────
export const useAdminMembers = (): UseQueryResult<AdminMembersResponse> =>
  useAdminRead(qk.adminMembers(), () => apiClient.admin.members(), MOCK_MEMBERS);

export const useAdminUserRoles = (userId: string, enabled = true): UseQueryResult<UserRolesDto> =>
  useAdminRead(
    qk.adminUserRoles(userId),
    () => apiClient.admin.userRoles(userId),
    MOCK_USER_ROLES[userId] ?? { user_id: userId, roles: [] },
    { enabled },
  );

// ── Rollen & Berechtigungsgruppen ─────────────────────────────────────────────
export const useAdminRoles = (): UseQueryResult<{ roles: RoleDto[] }> =>
  useAdminRead(qk.adminRoles(), () => apiClient.admin.roles(), { roles: MOCK_ROLES });

export const useAdminRolesCatalog = (): UseQueryResult<CatalogResponseDto> =>
  useAdminRead(qk.adminRolesCatalog(), () => apiClient.admin.rolesCatalog(), MOCK_CATALOG);

export const useVisibilityCatalog = (
  enabled = true,
): UseQueryResult<{ catalog: VisibilityKeyDefinition[] }> =>
  useAdminRead(
    qk.adminVisibilityCatalog(),
    () => apiClient.admin.visibilityCatalog(),
    { catalog: MOCK_VISIBILITY_CATALOG },
    { enabled },
  );

export const useRoleVisibilityDenies = (
  roleId: string,
  enabled = true,
): UseQueryResult<RoleVisibilityDeniesResponse> =>
  useAdminRead(
    qk.adminRoleVisibilityDenies(roleId),
    () => apiClient.admin.roleVisibilityDenies(roleId),
    { denied_keys: [] },
    { enabled },
  );

// ── Feldrechte (WS1 §5.2) ─────────────────────────────────────────────────────
export const useFieldPermissionCatalog = (
  enabled = true,
): UseQueryResult<{ catalog: FieldPermissionKeyDefinition[] }> =>
  useAdminRead(
    qk.adminFieldCatalog(),
    () => apiClient.admin.fieldCatalog(),
    { catalog: MOCK_FIELD_CATALOG },
    { enabled },
  );

export const useRoleFieldPermissions = (
  roleId: string,
  enabled = true,
): UseQueryResult<RoleFieldPermissionsResponse> =>
  useAdminRead(
    qk.adminRoleFieldPermissions(roleId),
    () => apiClient.admin.roleFieldPermissions(roleId),
    MOCK_FIELD_PERMISSIONS,
    { enabled },
  );

// ── Teams ─────────────────────────────────────────────────────────────────────
export const useFunktionsgruppen = (enabled = true): UseQueryResult<FunktionsgruppenListResponse> =>
  useAdminRead(
    qk.adminFunktionsgruppen(),
    () => apiClient.admin.funktionsgruppen(),
    { funktionsgruppen: MOCK_TEAMS },
    { enabled },
  );

export const useFunktionsgruppe = (
  id: string,
  enabled = true,
): UseQueryResult<FunktionsgruppeDetailDto> =>
  useAdminRead(
    qk.adminFunktionsgruppe(id),
    () => apiClient.admin.funktionsgruppe(id),
    MOCK_TEAM_DETAIL[id] ?? { ...MOCK_TEAMS[0]!, members: [] },
    { enabled },
  );

// ── Kundenteams (customer-visibility teams, Bereich 2, WS2) ─────────────────────
export const useKundenteams = (enabled = true): UseQueryResult<KundenteamListResponse> =>
  useAdminRead(qk.adminKundenteams(), () => apiClient.admin.kundenteams(), MOCK_KUNDENTEAMS, {
    enabled,
    // A 403 is the api's ANSWER, not a blip: retrying it can only repeat the refusal, and
    // this list sits on hot paths (Kunde detail, Mandant tab).
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 403) && failureCount < 1,
  });

/**
 * MV: the Kundenteam list behind the two mail-visibility controls (the Kunde detail's
 * "Post sichtbar für" and the Mandant tab's default team).
 *
 * Gated on `admin.team.read`, which is what GET /admin/teams requires, and NOT on the
 * `visibility.manage` the pointer WRITE needs: the two rights are independent, so either one
 * alone must neither fire a request that can only 403 nor hide names the viewer may read.
 */
export const useMailVisibilityTeams = (): UseQueryResult<KundenteamListResponse> => {
  const { can } = useEffectivePermissions();
  return useKundenteams(can('admin.team.read'));
};

export const useKundenteam = (
  id: string,
  enabled = true,
): UseQueryResult<KundenteamDetailDto> =>
  useAdminRead(
    qk.adminKundenteam(id),
    () => apiClient.admin.kundenteam(id),
    MOCK_KUNDENTEAM_DETAIL[id] ?? {
      ...MOCK_KUNDENTEAMS.teams[0]!,
      members: [],
      customers: [],
    },
    { enabled },
  );

// ── Kontrollfunktion / control backlog (Bereich 5, WS5) ─────────────────────────
export const useControlBacklog = (): UseQueryResult<ControlBacklogListResponse> =>
  useAdminRead(qk.adminControlBacklog(), () => apiClient.admin.controlBacklog(), MOCK_CONTROL_BACKLOG);

// ── Prozess-Erinnerungen / per-process-type reminder config + template (P7) ─────
export const useProcessReminderConfig = (): UseQueryResult<ProcessReminderConfigResponseDto> =>
  useAdminRead(
    qk.adminProcessReminderConfig(),
    () => apiClient.admin.processReminderConfig(),
    MOCK_PROCESS_REMINDER_CONFIG,
  );

// ── Mahnwesen P-04 / parameter set + Kommunikationsbausteine (P4-S2) ─────────────
export const useMahnwesenConfig = (
  processKey: 'P-04' | 'P-05' = 'P-04',
): UseQueryResult<MahnwesenProcessConfigDto> =>
  useAdminRead(
    qk.adminMahnwesenConfig(processKey),
    () => apiClient.admin.mahnwesenConfig(processKey),
    mockMahnwesenConfig(processKey),
  );

export const useTextTemplates = (
  processKey: 'P-04' | 'P-05' | 'P-06' = 'P-04',
): UseQueryResult<TextTemplateDto[]> =>
  useAdminRead(
    qk.adminTextTemplates(processKey),
    () => apiClient.admin.textTemplates(processKey),
    mockTextTemplates(processKey),
  );

// ── Rechnungskontrolle ────────────────────────────────────────────────────────
export const useToleranceConfig = (): UseQueryResult<ToleranceConfigEntryDto[]> =>
  useAdminRead(qk.adminToleranceConfig(), () => apiClient.admin.toleranceConfig(), MOCK_TOLERANCE);

// ── Regelwerk (P2 Placement Slice 3) ────────────────────────────────────────────
export const useRegelwerkSollDeckung = (
  filters: { ebene?: string; branchencode?: string; include_inactive?: boolean } = {},
): UseQueryResult<RegelwerkListResponse<RegelwerkSollDeckungItem>> =>
  useAdminRead(
    qk.adminRegelwerkSoll({
      ebene: filters.ebene,
      branchencode: filters.branchencode,
      include_inactive: filters.include_inactive ? 'true' : undefined,
    }),
    () => apiClient.admin.regelwerkSollDeckung(filters),
    MOCK_REGELWERK_SOLL,
  );

export const useRegelwerkAttributSchema = (
  filters: { ebene?: string; branchencode?: string; profil_typ?: string; include_inactive?: boolean } = {},
): UseQueryResult<RegelwerkListResponse<RegelwerkAttributSchemaItem>> =>
  useAdminRead(
    qk.adminRegelwerkSchema({
      ebene: filters.ebene,
      branchencode: filters.branchencode,
      profil_typ: filters.profil_typ,
      include_inactive: filters.include_inactive ? 'true' : undefined,
    }),
    () => apiClient.admin.regelwerkAttributSchema(filters),
    MOCK_REGELWERK_SCHEMA,
  );

// ── Ausschlussliste / customer-suppression (Fix 6) ────────────────────────────
export const useAdminSuppression = (): UseQueryResult<SuppressionEntryDto[]> =>
  useAdminRead(qk.adminSuppression(), () => apiClient.admin.suppression(), []);

// ── Berechtigungskonzept V3.0 · Bereich 1 (3.5) — the 3-tier e-mail blocklist ──
/** Mock mode reads the shared personal list (features/blocklist), so all screens agree. */
export const useEmailBlocklist = (): UseQueryResult<EmailBlacklistListResponse> => {
  const live = isLiveData();
  return useQuery<EmailBlacklistListResponse>({
    queryKey: qk.emailBlocklist(),
    queryFn: () =>
      live
        ? apiClient.emailBlocklist.list()
        : Promise.resolve({ rules: myBlocklistMockRules(), can_manage_tenant: false }),
  });
};

// ── Workflows ─────────────────────────────────────────────────────────────────
export const useChannelConfig = (enabled = true): UseQueryResult<ChannelConfigDto[]> =>
  useAdminRead(qk.adminChannelConfig(), () => apiClient.admin.channelConfig(), MOCK_CHANNELS, {
    enabled,
  });

export const useChannelMatrix = (enabled = true): UseQueryResult<ChannelMatrixEntryDto[]> =>
  useAdminRead(qk.adminChannelMatrix(), () => apiClient.admin.channelMatrix(), MOCK_CHANNEL_MATRIX, {
    enabled,
  });

export const useConfidencePolicy = (enabled = true): UseQueryResult<ConfidencePolicyEntryDto[]> =>
  useAdminRead(qk.adminConfidencePolicy(), () => apiClient.admin.confidencePolicy(), MOCK_CONFIDENCE, {
    enabled,
  });

export const useGroupRouting = (enabled = true): UseQueryResult<GroupRoutingEntryDto[]> =>
  useAdminRead(qk.adminGroupRouting(), () => apiClient.admin.groupRouting(), MOCK_GROUP_ROUTING, {
    enabled,
  });

// ── Mandant & Branding ────────────────────────────────────────────────────────
export const useTenantConfig = (enabled = true): UseQueryResult<TenantConfigDto> =>
  useAdminRead(qk.adminTenant(), () => apiClient.admin.tenant(), MOCK_TENANT, { enabled });

export const usePortalAdminConfig = (enabled = true): UseQueryResult<PortalConfigDto> =>
  useAdminRead(qk.adminPortalConfig(), () => apiClient.admin.portalConfig(), MOCK_PORTAL_CONFIG, {
    enabled,
  });

export const usePortalGrants = (
  subjectType: string,
  subjectId: string,
  enabled = true,
): UseQueryResult<PortalGrantAdminItem[]> =>
  useAdminRead(
    qk.adminPortalGrants(subjectType, subjectId),
    () => apiClient.admin.portalGrants(subjectType, subjectId),
    MOCK_PORTAL_GRANTS,
    { enabled: enabled && Boolean(subjectId) },
  );

export const usePortalAccess = (
  subjectType: string,
  subjectId: string,
  enabled = true,
): UseQueryResult<PortalAccessResponse> =>
  useAdminRead(
    qk.adminPortalAccess(subjectType, subjectId),
    () => apiClient.admin.portalAccess(subjectType, subjectId),
    MOCK_PORTAL_ACCESS,
    { enabled: enabled && Boolean(subjectId) },
  );

export const useAvbLibrary = (
  filters: { carrier_uid?: string; q?: string } = {},
  enabled = true,
): UseQueryResult<AvbDocumentResponse[]> =>
  useAdminRead(qk.adminAvb(filters), () => apiClient.admin.avbList(filters), MOCK_AVB, { enabled });

// ── System-Status + Audit ─────────────────────────────────────────────────────
export const useApiHealth = (): UseQueryResult<HealthStatus> =>
  useAdminRead(qk.adminHealth(), () => apiClient.health.get(), MOCK_HEALTH);

/**
 * useAuditLog — keyset ("Mehr laden") pagination over GET /audit. Flattens the
 * pages to one `rows` array; the page appends the next cursor's slice. Mock mode
 * resolves ONE deterministic page (next_cursor null → no "Mehr laden" in tests).
 * The useKundeRows infinite-query precedent — no manual accumulation, no double-append.
 */
export interface AuditLogResult {
  rows: AuditEntry[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}

export function useAuditLog(
  filters: Record<string, string | number | undefined> = {},
): AuditLogResult {
  const live = isLiveData();
  const query = useInfiniteQuery<AuditListResponse>({
    queryKey: qk.adminAudit(filters),
    queryFn: ({ pageParam }) =>
      live
        ? apiClient.admin.audit({ ...filters, cursor: pageParam as string | undefined })
        : Promise.resolve(MOCK_AUDIT),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.page.next_cursor ?? undefined,
    ...(live
      ? {}
      : { initialData: { pages: [MOCK_AUDIT], pageParams: [undefined] }, staleTime: Infinity }),
  });

  return {
    rows: query.data?.pages.flatMap((p) => p.data) ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
